import React, { useMemo } from "react";
import { useState } from "react";
import { Box, Chip, CircularProgress, Grid, LinearProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TitleBar from "components/TitleBar";
import ProcurementServiceListPopUp from "./procurementServiceListPopUp";

const colorPalette = ["#3B00ED", "#9C27B0", "#D81B60", "#FF9800"];

const agingData = [
  {
    label: "Project",
    value: 9,
    percentage: 64,
    color: colorPalette[0],
  },
  {
    label: "Marketing",
    value: 2,
    percentage: 14,
    color: colorPalette[1],
  },
  {
    label: "Asset Management",
    value: 0,
    percentage: 0,
    color: colorPalette[2],
  },
  {
    label: "Unknown",
    value: 3,
    percentage: 21,
    color: colorPalette[3],
  },
];



const WorkOrder = ({ data, isLoading, isError }) => {
  const [isViewTracker, setIsViewTracker] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const totalWorkOrder = data?.totalWorkOrders ?? 0;

  const departmentData = data?.departmentDistribution || []

  const totalCount = departmentData?.reduce((acc, curr) => acc + Number(curr.count ?? 0), 0) ?? 0

  const handleCategoryClick = (categoryName) => {

    setSelectedCategory(categoryName);
    setIsViewTracker(true);
  };

  const handleClose = () => {
    setIsViewTracker(false);
    setSelectedCategory(null);
  };

  const ageingData = departmentData?.map((item, index) => {
    const percentage = totalCount > 0 ? (Number(item.count) / totalCount) * 100 : 0
    const colorIndex = index % colorPalette?.length;
    const label = item.departmentName || "Unknown";
    const value = Number(item.count ?? 0);
    return {
      label,
      value,
      percentage,
      color: colorPalette[colorIndex]
    }

  })

  const options = useMemo(() => {
    return {
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        height: 170,
        width: 170,
        events: {
          render: function () {
            const chart = this;

            if (chart.customCenterText) {
              chart.customCenterText.destroy();
              chart.customSubText.destroy();
            }

            const centerX = chart.plotLeft + chart.plotWidth / 2;
            const centerY = chart.plotTop + chart.plotHeight / 2;

            chart.customCenterText = chart.renderer
              .text(`${totalWorkOrder}`, centerX, centerY - 5)
              .css({
                fontSize: "24px",
                fontWeight: "bold",
                fontFamily: "Outfit, sans-serif",
                color: "#000",
                textAnchor: "middle",
              })
              .attr({ align: "center" })
              .add();

            chart.customSubText = chart.renderer
              .text("WO", centerX, centerY + 18)
              .css({
                fontSize: "14px",
                fontFamily: "Outfit, sans-serif",
                color: "#737791",
                textAnchor: "middle",
              })
              .attr({ align: "center" })
              .add();
          },
        },
      },

      title: { text: null },

      plotOptions: {
        pie: {
          innerSize: "80%",
          dataLabels: { enabled: false },
          borderRadius: 0,
          borderWidth: 1,
          borderColor: "#fff",
          cursor: "pointer",
          point: {
            events: {
              click: function (event) {
                handleCategoryClick(this.name);
              }
            }
          }
        },
      },

      series: [
        {
          data: ageingData?.map((item) => ({
            name: item.label,
            y: item.percentage,
            color: item.color,
          })),
        },
      ],

      credits: { enabled: false },
      tooltip: {
        pointFormat:
          '<span style="color:{point.color}">\u25CF</span> {point.name}: <b>{point.y}%</b><br/>',
      },
    }
  }, [totalWorkOrder, ageingData])


  if (isLoading) {
    return (
      <Box sx={{
        minHeight: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{
        minHeight: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <Typography color="error">
          Failed to load work order data.
        </Typography>
      </Box>
    );
  }
  return (
    <>
      <Grid size={12}>
        <TitleBar title={"Work Order by Department"} description={"Work Orders Distribution by Department"} />
      </Grid>
      <Grid container sx={{ p: 2 }}>
        <Grid
          size={{ xs: 12, md: 8 }}
          sx={{ alignContent: "flex-start", mt: "19px" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              pl: "24px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: { xs: "18px", sm: "18px", md: "18px" },
                lineHeight: { xs: "16px", md: "24px" },
                color: "#000000",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              Total Work Order
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "28px",
                lineHeight: "36px",
                color: "#000000",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              {totalWorkOrder}
            </Typography>
            {/* <Typography
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#00000099",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              of value INR 95 Cr
            </Typography> */}
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              position: "relative",
              width: "160px",
              height: "160px",
              margin: "0 auto",
            }}
          >
            <HighchartsReact highcharts={Highcharts} options={options} />
          </Box>
        </Grid>

        <Grid size={12} sx={{ mt: 4 }}>
          {ageingData?.length > 0 ? ageingData.map((item, index) => (
            <Box
              key={index}
              sx={{ width: "100%", mt: index === 0 ? "15px" : "30px", p: "0" }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "#00000099",
                    fontFamily: "Outfit, sans-serif",
                    lineHeight: "20px",
                    letterSpacing: "0.25px",
                  }}
                >
                  {item.label}
                </Typography>
                <Chip
                  label={item.value}
                  sx={{
                    fontWeight: 600,
                    fontSize: "11px",
                    color: "#433F5E",
                    fontFamily: "Outfit, sans-serif",
                    bgcolor: "#E7E7EB",
                    p: "2px 16px",
                    borderRadius: "5px",
                    height: "auto",
                  }}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={item.percentage}
                sx={{
                  height: 4,
                  borderRadius: 5,
                  backgroundColor: "#E8EAED",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: item.color,
                    borderRadius: 5,
                  },
                }}
              />
            </Box>
          )) : <Box
            sx={{
              minHeight: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: "Outfit",
                color: "#98A2B3",
              }}
            >
              No Work order ageing data available.
            </Typography>
          </Box>}
        </Grid>
      </Grid>
      {isViewTracker && (
        <ProcurementServiceListPopUp
          header="Work Order By Department"
          open={isViewTracker}
          handleClose={handleClose}
          data={{ Department: selectedCategory }}

        />
      )}
    </>
  );
};

export default WorkOrder;
